import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const FAQ: React.FC = () => {
  return (
    <MainLayout isLoggedIn={false}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-center mb-8 text-primary-blue">Frequently Asked Questions</h1>
        
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="item-1">
            <AccordionTrigger className="text-lg font-medium">
              What is our referral program?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600 space-y-4">
              <p>Our referral program rewards you for sharing our services with friends, family, and your social network. Here's how it works:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>You get a unique referral code and link after signing up</li>
                <li>Share this link with potential customers</li>
                <li>When someone signs up using your link, you receive credit</li>
                <li>Earn points or cash rewards for successful referrals</li>
                <li>Track all your referrals and earnings in your dashboard</li>
              </ul>
              
              <div className="mt-6 border rounded-lg overflow-hidden shadow-sm">
                <img 
                  src="/lovable-uploads/843f56a9-ea90-4bfc-9000-a1b7e1408a94.png" 
                  alt="User Dashboard Referral Interface" 
                  className="mx-auto"
                  style={{ maxWidth: "700px", maxHeight: "500px" }}
                />
                <p className="text-xs text-center py-2 bg-gray-50">Your referral dashboard shows your unique code, referral stats, and earnings</p>
              </div>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-2">
            <AccordionTrigger className="text-lg font-medium">
              How do I earn money with referrals?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600 space-y-4">
              <p>Earning money through our referral program is straightforward:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Get your personal referral link</strong> from your dashboard</li>
                <li><strong>Share your link</strong> through various channels:
                  <ul className="list-circle pl-6 mt-1">
                    <li>Social media posts</li>
                    <li>Direct messages to friends</li>
                    <li>Email newsletters</li>
                    <li>Your website or blog</li>
                    <li>Professional networks</li>
                  </ul>
                </li>
                <li><strong>Track referrals</strong> in real-time on your dashboard</li>
                <li><strong>Earn rewards</strong> when your referrals sign up or make purchases</li>
                <li><strong>Cash out</strong> your earnings once you reach the minimum threshold</li>
              </ul>
              
              <p className="mt-2">The more people who use your link, the more you earn. There's no limit to how much you can make!</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-3">
            <AccordionTrigger className="text-lg font-medium">
              What are the best ways to share my referral link?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600">
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Social Media</strong>: Create engaging posts highlighting benefits and include your link</li>
                <li><strong>Personal Network</strong>: Share with friends, family, and colleagues who might benefit</li>
                <li><strong>Email Marketing</strong>: Include your link in your email signature or newsletters</li>
                <li><strong>Content Creation</strong>: Write blog posts or create videos about your experience</li>
                <li><strong>Online Communities</strong>: Share in relevant groups (following community guidelines)</li>
                <li><strong>Business Cards</strong>: Add a QR code linked to your referral link</li>
                <li><strong>Direct Messages</strong>: Send personalized invitations to potentially interested contacts</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-4">
            <AccordionTrigger className="text-lg font-medium">
              How do I track my referrals and earnings?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600">
              <p>Your referral dashboard provides comprehensive tracking tools:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Real-time Monitoring</strong>: See when someone clicks your link</li>
                <li><strong>Conversion Tracking</strong>: Know when a click converts to a signup</li>
                <li><strong>Status Updates</strong>: Track whether referrals are pending, approved, or rejected</li>
                <li><strong>Earnings Summary</strong>: View your current balance and lifetime earnings</li>
                <li><strong>Payout History</strong>: Access records of all past payments</li>
                <li><strong>Performance Analytics</strong>: See which sharing methods work best</li>
                <li><strong>Notifications</strong>: Get alerts when you earn rewards</li>
              </ul>
              
              <p className="mt-2">Login to your account and visit the "Referrals" tab to access all these tracking features.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-5">
            <AccordionTrigger className="text-lg font-medium">
              How do I receive my referral payments?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600">
              <p>We offer multiple payment methods for your convenience:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Direct Deposit</strong>: Funds sent directly to your bank account</li>
                <li><strong>PayPal</strong>: Quick transfers to your PayPal account</li>
                <li><strong>Store Credit</strong>: Apply earnings to your own purchases</li>
                <li><strong>Gift Cards</strong>: Select from various popular retailers</li>
                <li><strong>Cryptocurrency</strong>: Receive payments in selected cryptocurrencies (where available)</li>
              </ul>
              
              <p className="mt-2">Set your preferred payment method in your account settings. Payments are processed once you reach the minimum threshold of $25. Processing typically takes 3-5 business days.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-6">
            <AccordionTrigger className="text-lg font-medium">
              Is there a limit to how many people I can refer?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600">
              <p>There's no limit to the number of people you can refer! However, there are a few important points to keep in mind:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>Each plan has different referral rewards structures</li>
                <li>Enterprise customers may have custom referral agreements</li>
                <li>All referrals must comply with our terms of service</li>
                <li>Fraudulent referrals will be rejected and may result in account suspension</li>
                <li>Some regions may have specific regulatory requirements</li>
              </ul>
              
              <p className="mt-2">Our top referrers often bring in hundreds of new customers. The more quality referrals you make, the more you earn!</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-7">
            <AccordionTrigger className="text-lg font-medium">
              What makes a successful referral strategy?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600">
              <p>Based on our top referrers, here are proven strategies for success:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Target the Right Audience</strong>: Focus on people who genuinely need our service</li>
                <li><strong>Explain the Benefits</strong>: Clearly communicate how our product helps solve problems</li>
                <li><strong>Share Personal Success</strong>: Include how you've personally benefited from using our platform</li>
                <li><strong>Be Authentic</strong>: Honest recommendations perform better than sales pitches</li>
                <li><strong>Use Multiple Channels</strong>: Don't rely on just one method of sharing</li>
                <li><strong>Follow Up</strong>: Gentle reminders to people who showed interest</li>
                <li><strong>Provide Support</strong>: Offer to answer questions about the service</li>
              </ul>
              
              <p className="mt-2">Remember that quality referrals tend to generate better long-term results than quantity.</p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="item-8">
            <AccordionTrigger className="text-lg font-medium">
              How many SMS referral invitations can I send per day?
            </AccordionTrigger>
            <AccordionContent className="text-gray-600 space-y-4">
              <p>To prevent spam and ensure quality referrals, we've implemented a daily SMS sending limit:</p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Limit:</strong> 2 SMS referral invitations per 24-hour period</li>
                <li>The 24-hour period is calculated from the time of your first SMS sent</li>
                <li>Once you reach the limit, you'll need to wait before sending more SMS invitations</li>
                <li>This limit helps maintain the integrity of our referral system</li>
                <li>You can continue to refer friends through other channels like email or social media</li>
              </ul>
              
              <p className="mt-2 text-sm italic">
                Tip: If you need to send more invitations, consider using email or social media sharing options.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </MainLayout>
  );
};

export default FAQ;
